"use strict";
/**
 * Main file representing the full API of the library
 */
Object.defineProperty(exports, "__esModule", { value: true });
const parser_1 = require("./parser");
const Printer = require("./printer");
const Versification_1 = require("./Versification");
const Vidx = require("./vidx");
const Validate = require("./validate");
const RangeManip = require("./range-manip");
const Geometry = require("./geometry");
;
/**
 * Parses a string which may (or may not) represent a [[BibleRef]]
 * and returns a [[ParseResult]] representing whether the parse was successful,
 * and if so what value was obtained.
 *
 * Examples of valid input strings include:
 *
 * Book references (returns (list of) range(s) representing entire book):
 * - Genesis
 * - Gen
 * - GEN
 * - Genesis; Revelation
 *
 * Chapter references (returns (list of) range(s) representing entire chapters):
 * - Genesis 1
 * - GEN1
 * - Genesis 1; Exodus 2; REV1
 *
 * Verse References (represents a single verse):
 * - Genesis 1:1
 * - Gen 1v1
 * - GEN1.1
 *
 * Verse Ranges (continous blocks of verses, can cross chapter/book boundaries):
 * - Genesis 1:1-4
 * - Genesis 1:1 - 2:2
 * - Genesis 50 - Exodus 2
 *
 * Complex sets/combinations:
 * - Genesis 1:1, 2-4, 6
 * - GEN1.1-2; EXO3:4 - DEU5v6,10; Revelation 22
 *
 * @public
 * @param this - Instance of [[BibleRefLib]] (includes the versification to use)
 * @param str  - The string to parse
 */
function parse(str) {
    return parser_1.Parsers.BibleRef.parse(str);
}
/**
 * As with [[parse]], but throws error if the input string is not a valid representation
 * of a [[BibleRef]]
 *
 * @public
 * @param this - Instance of [[BibleRefLib]] (includes the versification to use)
 * @param str  - The string to parse
 */
function parseOrThrow(str) {
    let result = this.parse(str);
    if (result.status === true) {
        return result.value;
    }
    throw result;
}
/**
 * Parses the name of a book and returns either a string containing the USFM book id
 * or `null` if the book name was not recognised
 *
 * @public
 * @param this - Instance of [[BibleRefLib]] (includes the versification to use)
 * @param str  - The string to parse
 */
function parseBookName(str) {
    let result = parser_1.Parsers.Book.parse(str);
    if (result.status === true) {
        return result.value;
    }
    else {
        return null;
    }
}
/**
 * Converts a JSON [[BibleRef]] (or array thereof) into a human readable string
 *
 * The output format is fairly flexible, see [[FormatOptions]] for details
 */
function format(b, opts) {
    if ('length' in b) {
        return Printer.formatBibleRefList(this.versification, b, opts);
    }
    if (b.is_range) {
        return Printer.formatBibleRange(this.versification, b, opts);
    }
    else {
        return Printer.formatBibleVerse(this.versification, b, opts);
    }
}
/**
 * Takes an array of [[BibleRef]] instances and sorts them into
 * order as per the versification scheme in use, from the BibleRef
 * which appears first, to the one which apears last
 *
 * @note [[BibleRanges]] are compared based on their `start` value
 */
function sort(refs) {
    return refs.sort((a, b) => {
        let start = a.is_range ? a.start : a;
        let end = b.is_range ? b.start : b;
        let va = Vidx.toVidx(this.versification, start);
        let vb = Vidx.toVidx(this.versification, end);
        if (va == vb) {
            if (a.is_range && !b.is_range) {
                return 1;
            }
            if (!a.is_range && b.is_range) {
                return -1;
            }
            return 0;
        }
        return va - vb;
    });
}
/**
 * Converts a [[BibleVerse]] into a "Verse Index", IE: number between 0 and ~33K
 * Where 0 is the first verse of the Bible, and max value is the last verse of the Bible
 */
function toVidx(verse) {
    return Vidx.toVidx(this.versification, verse);
}
/**
 * Converts a "Verse Index" produced by [[toVidex]] back into a JSON [[BibleVerse]]
 *
 * @note You should ensure the versification scheme used is the same in both directions, else
 * unexpected results are likely to occur
 */
function fromVidx(vidx) {
    return Vidx.fromVidx(this.versification, vidx);
    ;
}
/**
 * Given a [[BibleRef]] instance, or array thereof, returns a new array of [[BibleRef]] instances
 * which contains at most `n` verses. The final [[BibleRange]] in input list may be split if its
 * length is too great
 *
 * @param refs - List of references to consider
 * @param n    - Maximum number of verses to include in output set
 *
 * @note This is a no-op if `countVerses(refs) < n`
 */
function firstNVerses(refs, n) {
    let data = 'length' in refs ? refs : [refs];
    return Vidx.firstNVerses(this.versification, data, n);
}
/**
 * Computes the total number of verses in the input [[BibleRef]] (or list thereof)
 */
function countVerses(refs) {
    if ('length' in refs) {
        return refs
            .map((r) => Vidx.countVerses(this.versification, r))
            .reduce((acc, x) => acc + x, 0);
    }
    else {
        return Vidx.countVerses(this.versification, refs);
    }
}
/**
 * Checks for issues with a [[BibleRef]] JSON object, such as the chapter or verse count
 * being out of bounds
 */
function validate(refs, include_warnings) {
    if ('length' in refs) {
        return refs
            .map((r) => Validate.validate(this.versification, r, include_warnings))
            .reduce((acc, x) => acc.concat(x), []);
    }
    else {
        return Validate.validate(this.versification, refs, include_warnings);
    }
}
/**
 * Attempts to fix issues identified by [[Validate]], for example by reducing the verse/chapter
 * number if it is too high
 */
function repair(ref, include_warnings) {
    return Validate.repair(this.versification, ref, include_warnings);
}
const constructFunc = function (v) {
    this.versification = v;
    this.parse = parse;
    this.parseOrThrow = parseOrThrow;
    this.format = format;
    this.sort = sort;
    this.toVidx = toVidx;
    this.fromVidx = fromVidx;
    this.firstNVerses = firstNVerses;
    this.countVerses = countVerses;
    this.validate = validate;
    this.repair = repair;
    this.makeRange = RangeManip.makeRange;
    this.splitByBook = RangeManip.splitByBook;
    this.splitByChapter = RangeManip.splitByChapter;
    this.splitByVerse = RangeManip.splitByVerse;
    this.iterateByBook = RangeManip.iterateByBook;
    this.iterateByChapter = RangeManip.iterateByChapter;
    this.iterateByVerse = RangeManip.iterateByVerse;
    this.combineRanges = RangeManip.combineRanges;
    this.nextChapter = RangeManip.nextChapter;
    this.previousChapter = RangeManip.previousChapter;
    this.nextBook = RangeManip.nextBook;
    this.previousBook = RangeManip.previousBook;
    this.getIntersection = Geometry.getIntersection;
    this.intersects = Geometry.intersects;
    this.getUnion = Geometry.getUnion;
    this.getDifference = Geometry.getDifference;
    this.contains = Geometry.contains;
    this.indexOf = Geometry.indexOf;
    this.verseAtIndex = Geometry.verseAtIndex;
    return this;
};
// Allow calling of all methods statically, with the default Versification scheme used
constructFunc.versification = Versification_1.default;
constructFunc.parse = parse.bind(constructFunc);
constructFunc.parseOrThrow = parseOrThrow.bind(constructFunc);
constructFunc.parseBookName = parseBookName.bind(constructFunc);
constructFunc.format = format.bind(constructFunc);
constructFunc.sort = sort.bind(constructFunc);
constructFunc.toVidx = toVidx.bind(constructFunc);
constructFunc.fromVidx = fromVidx.bind(constructFunc);
constructFunc.firstNVerses = firstNVerses.bind(constructFunc);
constructFunc.countVerses = countVerses.bind(constructFunc);
constructFunc.validate = validate.bind(constructFunc);
constructFunc.repair = repair.bind(constructFunc);
constructFunc.makeRange = RangeManip.makeRange.bind(constructFunc);
constructFunc.splitByBook = RangeManip.splitByBook.bind(constructFunc);
constructFunc.splitByChapter = RangeManip.splitByChapter.bind(constructFunc);
constructFunc.splitByVerse = RangeManip.splitByVerse.bind(constructFunc);
constructFunc.iterateByBook = RangeManip.iterateByBook.bind(constructFunc);
constructFunc.iterateByChapter = RangeManip.iterateByChapter.bind(constructFunc);
constructFunc.iterateByVerse = RangeManip.iterateByVerse.bind(constructFunc);
constructFunc.combineRanges = RangeManip.combineRanges.bind(constructFunc);
constructFunc.nextChapter = RangeManip.nextChapter.bind(constructFunc);
constructFunc.previousChapter = RangeManip.previousChapter.bind(constructFunc);
constructFunc.nextBook = RangeManip.nextBook.bind(constructFunc);
constructFunc.previousBook = RangeManip.previousBook.bind(constructFunc);
constructFunc.isFullBook = RangeManip.isFullBook.bind(constructFunc);
constructFunc.isFullChapter = RangeManip.isFullChapter.bind(constructFunc);
constructFunc.getIntersection = Geometry.getIntersection.bind(constructFunc);
constructFunc.intersects = Geometry.intersects.bind(constructFunc);
constructFunc.getUnion = Geometry.getUnion.bind(constructFunc);
constructFunc.getDifference = Geometry.getDifference.bind(constructFunc);
constructFunc.contains = Geometry.contains.bind(constructFunc);
constructFunc.indexOf = Geometry.indexOf.bind(constructFunc);
constructFunc.verseAtIndex = Geometry.verseAtIndex.bind(constructFunc);
exports.default = constructFunc;
